import { useEffect, useRef, useState } from "react"
import type { ReactNode, UIEvent } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import { useTableContext } from "./table.context"
import type { PageInfo } from "./table.types"
import TableHeader from "./TableHeader"
import TableBody from "./TableBody"
import TableEmpty from "./TableEmpty"
import TableEndOfList from "./TableEndOfList"
import TableLoadingBar from "./TableLoadingBar"
import TableSkeleton from "./TableSkeleton"
import { useTableSummary } from "./summary/table.summary.context"
import { useSummaryLoadAll } from "./summary/table.summary.loadAll"
import TableSummaryRow from "./summary/TableSummaryRow"

/** Fetch more triggers when remaining scroll distance is less than one third of the container height */
const FETCH_MORE_RATIO = 0.33

export type TableContentProps<_TData = unknown> = {
  className?: string
  /** True when any loading is happening (initial, refetch, or fetchMore) */
  isLoading?: boolean
  error?: Error | null
  renderError?(error: Error): ReactNode
  renderEmpty?: ReactNode
  /** Server pagination info from GraphQL */
  pageInfo?: PageInfo | null
  /**
   * Called to load the next page. Guards are handled internally. `limit`
   * asks for a page of that many rows — the summary row does, when it
   * fetches the rest of the list — and defaults to the caller's page size.
   */
  onFetchMore?(limit?: number): void
  /** Noun for the always-on "Showing X of Y" line. */
  countLabel?: string
}

/**
 * Content area.
 * - No data + loading → skeleton table
 * - Has data + loading → loading bar (refetch), plus placeholder rows below
 *   the last row while the next page is fetched
 * - No data + no loading → empty state
 *
 * Pages load on scroll, and — while the summary row shows on a
 * server-paginated list — on their own until the list is complete, so a
 * summary always ends up describing the whole view.
 */
function TableContent<TData = unknown>(props: TableContentProps<TData>) {
  const {
    className,
    isLoading = false,
    error = null,
    renderError,
    renderEmpty,
    pageInfo: pageInfoProp,
    onFetchMore: onFetchMoreProp,
    countLabel = "rows",
  } = props

  const {
    table,
    hasPersistence,
    infiniteScrollPageInfo,
    infiniteScrollFetchMore,
    setCountMeta,
  } = useTableContext<TData>()
  const { isVisible: isSummaryVisible } = useTableSummary()

  // Prefer caller-provided server pagination, fall back to the
  // table's own infinite-scroll synthesisers. This keeps server-
  // paginated tables (existing call sites) unchanged while letting
  // client-loaded tables opt into infinite scroll with one prop.
  const pageInfo = pageInfoProp ?? infiniteScrollPageInfo
  const onFetchMore = onFetchMoreProp ?? infiniteScrollFetchMore ?? undefined

  // Sync the "Showing X of Y" facts up to the toolbar's inline count.
  const serverTotal = pageInfoProp?.totalCount ?? null
  useEffect(() => {
    setCountMeta({ total: serverTotal, label: countLabel })
  }, [serverTotal, countLabel, setCountMeta])

  const { rows } = table.getRowModel()
  const hasData = rows.length > 0
  const isEmpty = !isLoading && !error && !hasData
  // Nothing to sum up without rows; the row also has nothing to sit under.
  const hasSummary = isSummaryVisible && hasData
  const hasNextPage = pageInfo?.hasNextPage ?? false
  const scrollRef = useRef<HTMLDivElement>(null)

  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const isFetchingMoreRef = useRef(false)
  // The end line only renders after at least one lazy load, so short
  // single-page lists don't grow a pointless "all loaded" footer.
  const [hasFetchedMore, setHasFetchedMore] = useState(false)

  // Stable ref for scroll handler
  const stateRef = useRef({ isLoading, hasNextPage, onFetchMore })
  stateRef.current = { isLoading, hasNextPage, onFetchMore }

  // Clear the fetchMore flag when new rows arrive — and also when loading
  // settles without any (failed or empty page), so the flag can't wedge
  // the placeholder rows on and block every later fetch.
  const rowCount = rows.length
  useEffect(() => {
    if (isFetchingMoreRef.current && !isLoading) {
      isFetchingMoreRef.current = false
      setIsFetchingMore(false)
    }
  }, [rowCount, isLoading])

  // Reset vertical scroll when sorting or filters change, but preserve horizontal scroll
  const { sorting, columnFilters } = table.getState()
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0
    }
  }, [sorting, columnFilters])

  // The one way a page gets loaded, whoever asks: nothing while a load is
  // already under way or there is no next page. Says whether it started one.
  function fetchMore(limit?: number): boolean {
    const { isLoading, hasNextPage, onFetchMore } = stateRef.current
    if (!onFetchMore || isLoading || !hasNextPage || isFetchingMoreRef.current)
      return false
    isFetchingMoreRef.current = true
    setIsFetchingMore(true)
    setHasFetchedMore(true)
    onFetchMore(limit)
    return true
  }

  function handleScroll(e: UIEvent<HTMLDivElement>) {
    const target = e.currentTarget
    const dist = target.scrollHeight - target.scrollTop - target.clientHeight
    const threshold = target.clientHeight * FETCH_MORE_RATIO
    if (dist < threshold) fetchMore()
  }

  // Only the server's paging is worth completing: a client-loaded table's
  // synthetic pages are all in memory already, and its summary covers them.
  const { isPartial: isSummaryPartial } = useSummaryLoadAll({
    isSummaryShown: hasSummary,
    pageInfo: pageInfoProp,
    isLoading,
    rowCount,
    fetchMore,
  })

  if (isLoading && !hasData) {
    return <TableSkeleton />
  }

  if (error && !hasData) {
    return (
      <div className="ui-table-error">
        {renderError ? renderError(error) : <ErrorMessage error={error} />}
      </div>
    )
  }

  return (
    <>
      <div
        ref={scrollRef}
        className={cn("ui-table-content", className)}
        onScroll={onFetchMore ? handleScroll : undefined}
      >
        <TableLoadingBar isLoading={(isLoading && hasData) || isFetchingMore} />

        <table
          className={cn("ui-table-content-table", {
            "ui-table-content-table-is-resizable": hasPersistence,
            "ui-table-content-table-has-summary": hasSummary,
          })}
        >
          <TableHeader />
          {isEmpty ? (
            <tbody>
              <tr>
                <td colSpan={table.getAllColumns().length}>
                  {renderEmpty === undefined ? (
                    <TableEmpty />
                  ) : (
                    <>{renderEmpty}</>
                  )}
                </td>
              </tr>
            </tbody>
          ) : (
            <TableBody<TData>
              rows={rows}
              scrollRef={scrollRef}
              isFetchingMore={isFetchingMore}
            />
          )}
          {hasSummary && (
            <TableSummaryRow<TData> isPartial={isSummaryPartial} />
          )}
        </table>

        <TableEndOfList
          isEndReached={hasFetchedMore && !hasNextPage && hasData}
          rowCount={rowCount}
          countLabel={countLabel}
        />
      </div>
    </>
  )
}

export default TableContent
